import { Anchor } from "@svea/design-system-components-react";
import { tw, useText } from "@sveabank/cc-common";
import { Card, CardHeader, P, Stack } from "@sveabank/cc-common";
import { Link } from "react-router";
import Image from "../../../components/image";
import { usePageContext } from "../../../hooks/context";
import type { Images } from "../../../images";
import type { OnboardingPackage } from "../../../types/package";
import { getTotalPackageCost } from "../helpers";
import { useChoosePackage } from "../hooks";
import { usePromotedPackage } from "../hooks/usePromotedPackage";
import { InterestDiscountTags } from "./interest-discount-tags";
import PackageBlockContent from "./package-block-content";
import PackageBlockFooter from "./package-block-footer";
import { PromotedTagWrapper } from "./promoted-tag-wrapper";

type Props = {
	bgColor: string;
	heading: string;
	headingColor?: string;
	onboardingPackage: OnboardingPackage;
	children: React.ReactNode;
	imageSrc: keyof typeof Images;
	moreAbout?: string;
	hasFromLabel?: boolean;
};

const PackageBlock = ({
	onboardingPackage,
	bgColor: headerBgColor,
	heading,
	headingColor = "text-text-headline",
	imageSrc,
	children,
	moreAbout = "",
	hasFromLabel = true,
}: Props) => {
	const [t, texts] = useText();
	const promotedPackage = usePromotedPackage();
	const { selectedPackageId } = usePageContext(useChoosePackage);
	const isPromoted = promotedPackage?.packageId === onboardingPackage.packageId;
	const isSelected = selectedPackageId === onboardingPackage.packageId;
	const text = texts.choosePackage[onboardingPackage.packageId];
	const fromLabel = hasFromLabel ? t(texts.common.from) : "";
	const moreAboutText = moreAbout || t(texts.common.moreAbout, heading);

	return (
		<PromotedTagWrapper packageId={onboardingPackage.packageId} isPromoted={isPromoted}>
			<Card
				classNames={tw(
					"flex h-full flex-col overflow-hidden rounded-lg shadow-md",
					isSelected && "outline-interactive-primary outline-3 outline-solid",
				)}
			>
				<CardHeader classNames={headerBgColor}>
					<Stack gap={2}>
						<P className={tw("text-sm uppercase", headingColor)}>{t(text.targetGroup)}</P>
						<h2
							className={tw(
								"tablet:text-[40px] pb-6 text-[36px] leading-[100%] font-bold",
								headingColor,
							)}
						>
							{heading}
						</h2>
					</Stack>
				</CardHeader>
				<PackageBlockContent>
					<div className="flex items-start gap-6">
						<div className="flex flex-1 flex-col gap-6">
							<Stack gap={6}>
								{children}

								<InterestDiscountTags onboardingPackage={onboardingPackage} />

								<Stack gap={2}>
									<P className="tablet:block hidden">{t(text.desktop.description)}</P>
									<P className="tablet:hidden block">{t(text.mobile.description)}</P>

									{onboardingPackage.packageId !== "FORETAGARNA-CARDONLY" && (
										<Anchor
											as={Link}
											to={t(text.externalLink)}
											target="_blank"
											iconEnd="externalLink"
										>
											{moreAboutText}
										</Anchor>
									)}
								</Stack>

								<PackageBlockFooter
									isPromoted={isPromoted}
									packageId={onboardingPackage.packageId}
									price={`${fromLabel ? fromLabel : ""} ${getTotalPackageCost(onboardingPackage)} ${t(texts.common.sekPerMonth)}`}
								/>
							</Stack>
						</div>

						{isPromoted && (
							<div className="tablet:block flex hidden flex-1 flex-col gap-6">
								<Image
									imageSrc={imageSrc}
									altText={heading}
									className="h-full w-full rounded-xl object-contain"
								/>
							</div>
						)}
					</div>
				</PackageBlockContent>
			</Card>
		</PromotedTagWrapper>
	);
};

export default PackageBlock;
